'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'motion/react';

interface MenuItem {
  label: string;
  href: string;
  description?: string;
  icon?: string;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
  featured?: MenuItem;
}

interface MegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  sections: MenuSection[];
}

const MegaMenu: React.FC<MegaMenuProps> = ({ isOpen, onClose, triggerRef, sections }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [left, setLeft] = useState<number>(0);
  const MENU_WIDTH = 360;



  // Position the menu under the trigger and keep it within the viewport
  useEffect(() => {
    if (!isOpen) return;
    const trigger = triggerRef.current as HTMLElement | null;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const padding = 8;
    const maxLeft = Math.max(0, window.innerWidth - MENU_WIDTH - padding);
    const desiredLeft = rect.left;
    setLeft(Math.min(Math.max(desiredLeft, padding), maxLeft));
  }, [isOpen, triggerRef]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={menuRef}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="absolute top-full bg-white border border-gray-200 rounded-lg shadow-lg z-[400]"
          style={{ left, width: MENU_WIDTH }}
        >
          <div className="py-2">
            {sections.map((section, sectionIndex) => (
              <div key={section.title} className={sectionIndex > 0 ? 'pt-2 mt-2 border-t border-gray-100' : ''}>
                <h3 className="px-3 pb-2 text-sm font-semibold text-gray-900">
                  {section.title}
                </h3>
                <div className="py-1">
                  {section.items.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={onClose}
                      className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MegaMenu;
